import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, FileDiff, FileText, MessageSquare, Video } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import PromptDiffView from '@/components/prompt-diff/PromptDiffView';
import promptVersionsService, {
  type VersionDiffResponse,
} from '@/lib/api/promptVersions';
import onboardingService, {
  type InterviewEntry,
  type SessionDetailResponse,
} from '@/lib/api/onboarding';
import type { KGSummaryResponse } from '@/lib/api/knowledgeGaps';
import { formatDate } from './helpers';

export function ResolvedBanner({
  orgId,
  summary,
}: {
  orgId: string;
  summary: KGSummaryResponse;
}) {
  const [open, setOpen] = useState(false);
  const [trainingOpen, setTrainingOpen] = useState(false);
  const hasDiff =
    !!summary.resolved_by_prompt_version_id &&
    summary.resolved_by_prompt_version_number != null;
  // Auto-regen diff is the parallel attribution for training-resolved gaps.
  // Both fields must be populated — id alone (without server-enriched
  // version number) means the JOIN failed and the diff endpoint can't be
  // called by integer version, so the button stays hidden.
  const hasAutoRegenDiff =
    !!summary.auto_regen_prompt_version_id &&
    summary.auto_regen_prompt_version_number != null;
  const hasAttachedAsset =
    !!summary.resolved_by_asset_type && !!summary.resolved_by_asset_title;
  // Training-session resolution requires both ids. The interviewer id is
  // server-enriched (best-effort), so a session id without an interviewer id
  // means the JOIN failed — hide the button rather than render a dead-link.
  const hasTrainingSession =
    !!summary.resolved_by_training_session_id &&
    !!summary.resolved_by_training_session_interviewer_id;
  const trainingInterviewerId =
    summary.resolved_by_training_session_interviewer_id;
  const trainingSessionId = summary.resolved_by_training_session_id;
  const AssetIcon =
    summary.resolved_by_asset_type === 'video' ? Video : FileText;

  // Resolving prompt version, enriched onto the summary server-side. The
  // single-version /diff endpoint compares N→N+1 ("what changed after this
  // version") — but for the resolved banner we want the change that
  // *introduced* this version (prior→N). So we fetch the versions list,
  // pick the largest version strictly less than the resolving one, and
  // hit /diff with both endpoints explicit.
  //
  // Falls back to the auto-regen-produced version for training-resolved
  // gaps — the diff modal logic is identical regardless of which path
  // produced the version, only the source field differs. ``hasDiff`` and
  // ``hasAutoRegenDiff`` correspond to mutually-exclusive resolution paths;
  // if both are somehow set we prefer the prompt-update attribution since
  // it carries explicit admin review semantics.
  const afterVersion =
    summary.resolved_by_prompt_version_number ??
    summary.auto_regen_prompt_version_number;

  // Versions list — shares the ['prompt-versions', orgId] cache key with
  // AgentsPage / SalesAgentPage so the three surfaces hit one entry.
  // Gated on `open && (hasDiff || hasAutoRegenDiff)` so auto-regen-driven
  // diffs (training-resolved gaps) reuse the same lookup path as
  // optimizer-driven diffs (prompt-update-resolved gaps).
  const {
    data: versionsList,
    isLoading: versionsListLoading,
    isError: versionsListError,
  } = useQuery({
    queryKey: ['prompt-versions', orgId],
    queryFn: async () => {
      const res = await promptVersionsService.listVersions(orgId);
      if (!res.success || !res.data) {
        throw new Error(res.error || 'Failed to load prompt versions');
      }
      return res.data;
    },
    enabled: open && (hasDiff || hasAutoRegenDiff),
    staleTime: 5 * 60_000,
  });

  // Largest version strictly less than the resolving one. Null when the
  // resolving version is the very first version overall, or while the
  // versions list hasn't loaded yet.
  const priorVersion = useMemo<number | null>(() => {
    if (afterVersion == null || !versionsList) return null;
    let best: number | null = null;
    for (const v of versionsList.versions) {
      if (v.version < afterVersion && (best === null || v.version > best)) {
        best = v.version;
      }
    }
    return best;
  }, [versionsList, afterVersion]);

  // Diff for prior→resolving. Passing both endpoints explicitly bypasses
  // PromptDiffView's is_latest / is_first_version short-circuits. Cache
  // key shape matches SalesAgentPage's two-version comparison so the two
  // surfaces dedupe when looking at the same pair.
  const {
    data: versionDiffRes,
    isLoading: versionDiffLoading,
    isError: versionDiffError,
  } = useQuery({
    queryKey: [
      'prompt-version-diff',
      orgId,
      priorVersion != null && afterVersion != null
        ? `${priorVersion}-${afterVersion}`
        : null,
    ],
    queryFn: async () => {
      const res = await promptVersionsService.getVersionDiffBetween(
        orgId,
        priorVersion as number,
        afterVersion as number,
      );
      if (!res.success || !res.data) {
        throw new Error(res.error || 'Failed to load version diff');
      }
      return res.data as VersionDiffResponse;
    },
    enabled: open && priorVersion != null && afterVersion != null,
    staleTime: 5 * 60_000,
  });

  // Training session transcript — gated on the modal being open so we don't
  // fetch for banners that never get clicked. Cache key matches
  // InterviewerDetailPage's `getInterviewerSessionDetail` query so the two
  // surfaces dedupe when looking at the same session.
  const {
    data: trainingSession,
    isLoading: trainingSessionLoading,
    isError: trainingSessionError,
  } = useQuery({
    queryKey: [
      'interviewer-session-detail',
      trainingInterviewerId,
      trainingSessionId,
    ],
    queryFn: async () => {
      const res = await onboardingService.getInterviewerSessionDetail(
        trainingInterviewerId as string,
        trainingSessionId as string,
      );
      if (!res.success || !res.data) {
        throw new Error(res.error || 'Failed to load training session');
      }
      return res.data as SessionDetailResponse;
    },
    enabled:
      trainingOpen &&
      hasTrainingSession &&
      !!trainingInterviewerId &&
      !!trainingSessionId,
    staleTime: 5 * 60_000,
  });

  const attribution = (() => {
    const resolvedAt = summary.resolved_at ? formatDate(summary.resolved_at) : null;
    const name = summary.resolved_by_display_name ?? null;
    if (name && resolvedAt) return `by ${name} on ${resolvedAt}`;
    if (resolvedAt) return `on ${resolvedAt}`;
    if (name) return `by ${name}`;
    return '';
  })();

  // Modal states (mutually exclusive in render order below):
  // - errored:  either query failed
  // - empty:    versions loaded but no prior exists (resolving = first version)
  // - success:  versionDiffRes is in hand → render PromptDiffView
  // - loading:  any query still in flight
  // The same modal drives both resolution paths (prompt-update via hasDiff
  // and training-session+auto-regen via hasAutoRegenDiff) — the diff data is
  // structurally identical, only the source field differs.
  const diffModalActive = open && (hasDiff || hasAutoRegenDiff);
  const modalIsErrored =
    diffModalActive && (versionsListError || versionDiffError);
  const modalIsEmpty =
    diffModalActive && !!versionsList && priorVersion == null;
  const modalIsLoading =
    diffModalActive &&
    !modalIsErrored &&
    !modalIsEmpty &&
    !versionDiffRes &&
    (versionsListLoading || (priorVersion != null && versionDiffLoading));
  const versionHistoryPath =
    afterVersion != null
      ? priorVersion != null
        ? `/agents/configure?base=${priorVersion}&compare=${afterVersion}&selected=${afterVersion}`
        : `/agents/configure?version=${afterVersion}`
      : null;

  const headlineLabel = hasDiff
    ? 'Resolved'
    : hasAttachedAsset
      ? 'Resolved with knowledge source'
      : hasTrainingSession
        ? 'Resolved via training session'
        : 'Resolved manually';

  return (
    <div className="px-4 py-3 border-b card-border bg-card">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
          <span className="text-sm font-medium">{headlineLabel}</span>
          {attribution && (
            <span className="text-xs text-muted-foreground truncate">
              {attribution}
            </span>
          )}
        </div>
        {hasDiff && (
          <Button
            variant="default"
            size="sm"
            onClick={() => setOpen(true)}
            aria-label="View prompt changes"
          >
            <FileDiff className="h-4 w-4 mr-1.5" />
            View changes
          </Button>
        )}
        {hasTrainingSession && !hasDiff && (
          <Button
            variant={hasAutoRegenDiff ? 'outline' : 'default'}
            size="sm"
            onClick={() => setTrainingOpen(true)}
            aria-label="View training session"
          >
            <MessageSquare className="h-4 w-4 mr-1.5" />
            View training session
          </Button>
        )}
        {hasAutoRegenDiff && !hasDiff && (
          <Button
            variant="default"
            size="sm"
            onClick={() => setOpen(true)}
            aria-label="View prompt changes"
          >
            <FileDiff className="h-4 w-4 mr-1.5" />
            View prompt changes
          </Button>
        )}
      </div>
      {hasAttachedAsset && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1.5">
          <AssetIcon className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">
            <span className="text-muted-foreground">Resolved with: </span>
            <span className="font-medium text-foreground">
              {summary.resolved_by_asset_title}
            </span>
          </span>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-border/40 shrink-0">
            <DialogTitle>Prompt changes</DialogTitle>
            <DialogDescription className="sr-only">
              Diff of the prompt version that resolved this knowledge gap.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-5">
            {modalIsErrored ? (
              <p className="text-sm text-destructive">
                Failed to load prompt changes.
              </p>
            ) : modalIsEmpty ? (
              <p className="text-sm text-muted-foreground">
                Initial prompt version — no prior version to compare against.
              </p>
            ) : versionDiffRes ? (
              <PromptDiffView data={versionDiffRes} showSummaryCards={false} />
            ) : modalIsLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
              </div>
            ) : null}
          </div>

          <div className="px-6 py-3 border-t border-border/40 shrink-0 flex items-center justify-between gap-4">
            <span className="text-xs text-muted-foreground truncate">
              {attribution}
            </span>
            {versionHistoryPath && (
              <Link
                to={versionHistoryPath}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline shrink-0"
              >
                Open in version history →
              </Link>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={trainingOpen} onOpenChange={setTrainingOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-border/40 shrink-0">
            <DialogTitle>Training session</DialogTitle>
            <DialogDescription className="sr-only">
              Chat transcript of the training session that resolved this knowledge gap.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-5">
            {trainingSessionError ? (
              <p className="text-sm text-destructive">
                Failed to load training session.
              </p>
            ) : trainingSession ? (
              <div className="space-y-3">
                {trainingSession.entries.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No transcript available for this session.
                  </p>
                ) : (
                  trainingSession.entries.map((entry: InterviewEntry) => (
                    <div
                      key={entry.id}
                      className={
                        entry.speaker === 'interviewer'
                          ? 'flex justify-start'
                          : 'flex justify-end'
                      }
                    >
                      <div
                        className={
                          entry.speaker === 'interviewer'
                            ? 'max-w-[85%] rounded-2xl px-4 py-3 text-sm bg-muted text-foreground'
                            : 'max-w-[85%] rounded-2xl px-4 py-3 text-sm bg-primary text-primary-foreground'
                        }
                      >
                        <p className="whitespace-pre-wrap">{entry.content}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            ) : trainingSessionLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-16 w-3/4" />
                <Skeleton className="h-16 w-3/4 ml-auto" />
                <Skeleton className="h-16 w-3/4" />
              </div>
            ) : null}
          </div>

          <div className="px-6 py-3 border-t border-border/40 shrink-0 flex items-center gap-4">
            <span className="text-xs text-muted-foreground truncate">
              {attribution}
            </span>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
